import { useCallback, useEffect, useMemo } from "react";
import { useEntryViewState } from "@/shared/contexts/EntryViewStateContext";
import {
	useAllowedTimekeeperIds,
	useCurrentTimekeeperId,
} from "../timekeepers";
import type { FieldOptions } from "./FilterMenu";
import { normalizeOptions } from "./fields";
import type {
	EntryFilterState,
	EnumFilterField,
	NonStatusMultiSelectFilterField,
} from "./types";

const ALL_STATUSES = ["tracked", "reviewed", "exported", "deleted"];
const ATTORNEY_DEFAULT_STATUSES = ["tracked", "reviewed"] as const;

function optionValues(
	fieldOptions: FieldOptions,
	field: EnumFilterField,
): string[] {
	return normalizeOptions(fieldOptions[field] ?? []).map(
		(option) => option.value,
	);
}

export interface SharedFilterOptions {
	defaultStatuses?: readonly string[];
	/** Undefined means the permission/options list is not ready yet. */
	allowedTimekeeperIds?: readonly string[];
	/** Attorney workspaces default to the authenticated user's entries. */
	defaultTimekeeperId?: string;
}

/**
 * Shared in-memory filters for Attorney/Admin Entries and Calendar surfaces.
 * Timekeeper selections live in the provider and Attorney consumers normalize
 * them against their current permission list before querying.
 */
export function useFilters(
	fieldOptions: FieldOptions,
	options: SharedFilterOptions = {},
) {
	const { filterOverrides, setFilterOverrides, resetFilters } =
		useEntryViewState();
	const defaultStatuses = options.defaultStatuses ?? ATTORNEY_DEFAULT_STATUSES;
	const defaultTimekeeperIds = useMemo(() => {
		if (!options.defaultTimekeeperId) return [];
		const availableIds = new Set(optionValues(fieldOptions, "timekeeper"));
		return availableIds.has(options.defaultTimekeeperId)
			? [options.defaultTimekeeperId]
			: [];
	}, [fieldOptions, options.defaultTimekeeperId]);

	const filters = useMemo<EntryFilterState>(() => {
		const next: EntryFilterState = {
			...filterOverrides,
			status: filterOverrides.status ?? [...defaultStatuses],
		};
		const rawTimekeepers =
			filterOverrides.timekeeper ??
			(options.defaultTimekeeperId && defaultTimekeeperIds.length > 0
				? defaultTimekeeperIds
				: undefined);
		if (rawTimekeepers !== undefined) {
			if (options.allowedTimekeeperIds === undefined) {
				next.timekeeper = [];
			} else {
				const allowed = new Set(options.allowedTimekeeperIds);
				next.timekeeper = rawTimekeepers.filter((id) => allowed.has(id));
			}
		}
		return next;
	}, [
		filterOverrides,
		defaultStatuses,
		options.allowedTimekeeperIds,
		options.defaultTimekeeperId,
		defaultTimekeeperIds,
	]);

	const defaultFilters = useMemo<EntryFilterState>(
		() => ({
			status: [...defaultStatuses],
			...(defaultTimekeeperIds.length > 0
				? { timekeeper: defaultTimekeeperIds }
				: {}),
		}),
		[defaultStatuses, defaultTimekeeperIds],
	);

	useEffect(() => {
		if (
			!options.defaultTimekeeperId ||
			filterOverrides.timekeeper === undefined ||
			options.allowedTimekeeperIds === undefined
		)
			return;
		const normalized = filters.timekeeper ?? [];
		if (
			filterOverrides.timekeeper.length === normalized.length &&
			filterOverrides.timekeeper.every((id) => normalized.includes(id))
		)
			return;
		setFilterOverrides((current) => ({
			...current,
			timekeeper: normalized,
		}));
	}, [
		filterOverrides.timekeeper,
		filters.timekeeper,
		options.allowedTimekeeperIds,
		options.defaultTimekeeperId,
		setFilterOverrides,
	]);

	const replaceField = useCallback(
		(field: EnumFilterField, values: readonly string[] | undefined) => {
			setFilterOverrides((current) => {
				const next = { ...current };
				if (values === undefined) delete next[field];
				else next[field] = [...values];
				return next;
			});
		},
		[setFilterOverrides],
	);

	const setFieldValues = useCallback(
		(field: NonStatusMultiSelectFilterField, values: readonly string[]) =>
			replaceField(field, values),
		[replaceField],
	);
	const setStatus = useCallback(
		(values: readonly string[]) => replaceField("status", values),
		[replaceField],
	);
	const setTimekeepers = useCallback(
		(values: readonly string[]) => replaceField("timekeeper", values),
		[replaceField],
	);

	const toggleFieldValue = useCallback(
		(field: EnumFilterField, value: string) => {
			if (field === "status") {
				const has = filters.status.includes(value);
				setStatus(
					has
						? filters.status.filter((current) => current !== value)
						: [...filters.status, value],
				);
				return;
			}

			// Preserve timekeepers that are not visible in the current workspace.
			const rawValues = filters[field];
			const base = rawValues ?? optionValues(fieldOptions, field);
			const has = base.includes(value);
			replaceField(
				field,
				has ? base.filter((current) => current !== value) : [...base, value],
			);
		},
		[fieldOptions, filters, replaceField, setStatus],
	);

	const selectAll = useCallback(
		(field: EnumFilterField) => {
			if (field === "status") replaceField(field, ALL_STATUSES);
			else if (field === "timekeeper")
				replaceField(field, optionValues(fieldOptions, field));
			else replaceField(field, undefined);
		},
		[fieldOptions, replaceField],
	);
	const selectNone = useCallback(
		(field: EnumFilterField) => replaceField(field, []),
		[replaceField],
	);
	return {
		filters,
		defaultFilters,
		isReady: true,
		setFieldValues,
		toggleFieldValue,
		selectAll,
		selectNone,
		reset: resetFilters,
		setStatus,
		setTimekeepers,
	};
}

export function useAttorneyFilters(fieldOptions: FieldOptions) {
	const allowedTimekeeperIds = useAllowedTimekeeperIds();
	const currentTimekeeperId = useCurrentTimekeeperId();
	return useFilters(fieldOptions, {
		allowedTimekeeperIds,
		defaultTimekeeperId: currentTimekeeperId,
	});
}
